import { useState, useMemo, useEffect } from 'react'
import { ChevronUp, ChevronDown, CheckCircle, XCircle } from 'lucide-react'
import type { Exercise } from '@/types'
import { shuffleArray } from '@/lib/exerciseUtils'
import EditableText from '@/components/edit/EditableText'
import { useEditMode } from '@/contexts/EditModeContext'
import { useAuth } from '@/contexts/AuthContext'
import { saveArrayItem } from '@/lib/editApi'

interface Props {
  exercise: Exercise
  onSetOrder: (items: string[]) => void
  isSubmitted: boolean
  isCorrect: boolean
  onExerciseUpdated?: (updated: Exercise) => void
}

export default function OrderingExercise({ exercise, onSetOrder, isSubmitted, isCorrect, onExerciseUpdated }: Props) {
  const correctOrder = exercise.options || []
  const { isEditMode } = useEditMode()
  const { profile } = useAuth()

  // Shuffle solo una vez al montar
  const initialShuffle = useMemo(() => {
    let shuffled = shuffleArray(correctOrder)
    // Asegurar que no quede en el mismo orden
    let attempts = 0
    while (shuffled.every((item, i) => item === correctOrder[i]) && attempts < 10) {
      shuffled = shuffleArray(correctOrder)
      attempts++
    }
    return shuffled
  }, [exercise.id])

  const [items, setItems] = useState<string[]>(initialShuffle)

  // En modo edición mostramos los items en su ORDEN CORRECTO (no shuffled)
  // para que el profesor vea y edite la secuencia tal cual está guardada.
  useEffect(() => {
    if (isEditMode) setItems(correctOrder)
    else setItems(initialShuffle)
  }, [isEditMode, exercise.id])

  const saveItem = (index: number) => async (newText: string) => {
    if (!profile?.id) return { error: new Error('No autenticado') }
    const { error, newArray } = await saveArrayItem({
      table: 'exercises', id: exercise.id, field: 'options',
      currentArray: correctOrder, index, newItemValue: newText, editedBy: profile.id
    })
    if (!error) onExerciseUpdated?.({ ...exercise, options: newArray })
    return { error }
  }

  const moveItem = (index: number, direction: 'up' | 'down') => {
    if (isSubmitted) return
    const newIndex = direction === 'up' ? index - 1 : index + 1
    if (newIndex < 0 || newIndex >= items.length) return

    const newItems = [...items]
    ;[newItems[index], newItems[newIndex]] = [newItems[newIndex], newItems[index]]
    setItems(newItems)
    onSetOrder(newItems)
  }

  const getItemColor = (item: string, index: number) => {
    if (!isSubmitted) return 'border-gray-200 bg-white'
    if (item === correctOrder[index]) return 'border-green-500 bg-green-50'
    return 'border-red-500 bg-red-50'
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500 text-center mb-2">
        Usa las flechas para ordenar los elementos correctamente
      </p>

      <div className="space-y-2">
        {items.map((item, index) => (
          <div
            key={`${item}-${index}`}
            className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all ${getItemColor(item, index)}`}
          >
            {/* Posicion */}
            <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
              isSubmitted
                ? item === correctOrder[index]
                  ? 'bg-green-500 text-white'
                  : 'bg-red-500 text-white'
                : 'bg-gray-100 text-gray-600'
            }`}>
              {index + 1}
            </span>

            {/* Texto */}
            {isEditMode ? (
              <EditableText
                as="span"
                value={item}
                customSave={saveItem(index)}
                placeholder="(item vacío)"
                className="flex-1 font-medium text-gray-900"
              />
            ) : (
              <span className="flex-1 font-medium text-gray-900">{item}</span>
            )}

            {/* Flechas o iconos de resultado */}
            {!isEditMode && (isSubmitted ? (
              item === correctOrder[index]
                ? <CheckCircle className="w-5 h-5 text-green-500" />
                : <XCircle className="w-5 h-5 text-red-500" />
            ) : (
              <div className="flex flex-col">
                <button
                  type="button"
                  onClick={() => moveItem(index, 'up')}
                  disabled={index === 0}
                  className="p-1 hover:bg-gray-100 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronUp className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => moveItem(index, 'down')}
                  disabled={index === items.length - 1}
                  className="p-1 hover:bg-gray-100 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronDown className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Mostrar orden correcto si fallo */}
      {isSubmitted && !isCorrect && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-xs font-medium text-green-600 mb-2">Orden correcto:</p>
          <div className="space-y-1">
            {correctOrder.map((item, i) => (
              <p key={i} className="text-sm text-green-800">
                {i + 1}. {item}
              </p>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
